// Inlined rather than emitted as its own chunk: the library build ships as a
// self-contained ES bundle, and a `new Worker(new URL("/assets/…"))` reference
// is unresolvable for the webpack builds that consume it.
import BimIndexWorker from "./bimIndex.worker?worker&inline";
import type { Method, Requests, WorkerRequest, WorkerResponse } from "./protocol";

/**
 * Main-thread handle on the BIM-index worker: a promise-per-request RPC client.
 *
 * One instance owns one worker, and therefore one loaded index. `terminate()`
 * drops the worker, which is the only way to give its WASM linear memory back to
 * the browser — but it runs no code there, so send `close` first when the OPFS
 * files need to be unlocked at a defined moment.
 */
export class BimIndexClient {
    private readonly worker: Worker;
    private readonly pending = new Map<number, { resolve: (value: unknown) => void; reject: (error: Error) => void }>();
    private nextId = 1;

    constructor() {
        this.worker = new BimIndexWorker({ name: "bim-index" });
        this.worker.onmessage = (event: MessageEvent<WorkerResponse>) => {
            const message = event.data;
            const entry = this.pending.get(message.id);
            if (!entry) {
                return;
            }
            this.pending.delete(message.id);
            if (message.ok) {
                entry.resolve(message.result);
            } else {
                entry.reject(new Error(message.error));
            }
        };
        this.worker.onerror = (event) => {
            const error = new Error(event.message || "BIM index worker failed");
            for (const [, entry] of this.pending) {
                entry.reject(error);
            }
            this.pending.clear();
        };
    }

    /**
     * `transfer` hands the params' buffers over instead of copying them, for a
     * request that carries bulk bytes — a served result set is megabytes. The
     * caller's own views are detached once it returns, so pass only buffers it is
     * done with. Results come back transferred already (see the worker's
     * `transferable`).
     */
    call<M extends Method>(
        method: M,
        params: Requests[M]["params"],
        transfer?: Transferable[],
    ): Promise<Requests[M]["result"]> {
        const id = this.nextId++;
        const request = { kind: "request", id, method, params } as WorkerRequest;
        return new Promise((resolve, reject) => {
            this.pending.set(id, { resolve: resolve as (value: unknown) => void, reject });
            this.worker.postMessage(request, transfer ?? []);
        });
    }

    terminate() {
        this.worker.terminate();
        for (const [, entry] of this.pending) {
            entry.reject(new Error("BIM index worker terminated"));
        }
        this.pending.clear();
    }
}
